import { loginApiClient } from '@/core/infrastructure/api/apiClient';
import { parseJwt } from '@/core/utils/jwtUtils';
import { translate } from '@/core/utils/translate';

interface AuthState {
  accessToken: string;
  refreshToken: string;
  idToken?: string;
  user?: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
  };
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
}

interface DecodedToken {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string[];
}

const getStore = async () => {
  const storeModule = await import('@/store/store');
  return storeModule.store;
};

const dispatchAuthAction = async (type: 'auth/logout' | 'auth/restoreState', payload?: unknown) => {
  const reduxStore = await getStore();
  reduxStore.dispatch({ type, payload });
};

export const refreshAccessToken = async (): Promise<string | null> => {
  try {
    const authState = getAuthStateFromStorage();
    if (!authState.refreshToken) {
      await logoutUser();
      return null;
    }
    const response = await sendRefreshTokenRequest(authState);

    // Yanıtı işle ve token'ları güncelle
    return await processTokenResponse(response, authState);
  } catch (error: any) {
    await handleRefreshError(error);
    return null;
  }
};

/**
 * LocalStorage'dan auth bilgisini alır
 */
const getAuthStateFromStorage = (): AuthState => {
  const authStateStr = localStorage.getItem('authState') || '{}';
  const authState = JSON.parse(authStateStr);

  return {
    accessToken: authState.accessToken,
    refreshToken: authState.refreshToken,
    idToken: authState.idToken,
    user: authState.user,
  };
};

/**
 * Token yenileme isteği gönderir
 */
const sendRefreshTokenRequest = async (authState: AuthState) => {
  const lang = localStorage.getItem('i18nextLng') === 'tr' ? 'tr-TR' : 'en-US';
  return await loginApiClient.post<TokenResponse>(
    '',
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: 'merzigogateway',
      client_secret: 'merzigogateway_secret',
      refresh_token: authState.refreshToken,
    }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Authorization: `Bearer ${authState.accessToken}`,
        'Accept-Language': lang,
      },
    }
  );
};

/**
 * Token yanıtını işler ve depolamayı günceller
 */
const processTokenResponse = async (response: any, authState: AuthState): Promise<string> => {
  const { access_token, refresh_token, id_token } = response.data;

  if (!access_token || !refresh_token) {
    throw new Error(translate('errors/api:token_data_missing'));
  }

  // Token'ı decode et
  const decodedToken = parseJwt(access_token) as DecodedToken;
  if (!decodedToken) {
    throw new Error(translate('errors/api:token_invalid'));
  }

  // Auth state'i güncelle
  const updatedAuthState = {
    accessToken: access_token,
    refreshToken: refresh_token,
    idToken: id_token || authState.idToken,
    user: {
      username: decodedToken.username,
      email: decodedToken.email,
      firstName: decodedToken.firstName,
      lastName: decodedToken.lastName,
      roles: decodedToken.role || [],
    },
  };

  // Güncellenmiş state'i kaydet
  localStorage.setItem(
    'authState',
    JSON.stringify({
      ...updatedAuthState,
      isAuthenticated: true,
    })
  );

  // Redux state'i güncelle
  await dispatchAuthAction('auth/restoreState', {
    ...updatedAuthState,
    isAuthenticated: true,
  });

  return access_token;
};

/**
 * Hata durumunu işler
 */
const handleRefreshError = async (error: any): Promise<void> => {
  void error;
  await logoutUser();
};

/**
 * Kullanıcıyı güvenli bir şekilde çıkış yapar
 */
const logoutUser = async (): Promise<void> => {
  await dispatchAuthAction('auth/logout');
};
